import type { Table } from "dexie";
import type { Clock } from "@/domain/clock";
import { compareDateKeys, type DateKey } from "@/domain/dates";
import type { Entry, Persisted, Subscription, UserSettings } from "@/domain/entities";
import { BuiltInScenarioRepository } from "../BuiltInScenarioRepository";
import {
  ChangeEmitter, DEFAULT_SETTINGS,
  type DateRange, type EntryRepository, type Repository, type SettingsRepository, type Store, type SubscriptionRepository,
} from "../repositories";
import { UmemoryDatabase, type SettingsRow } from "./UmemoryDatabase";

function alive<T extends Persisted>(row: T | undefined): T | undefined {
  return row && !row.deleted ? row : undefined;
}

class DexieRepository<T extends Persisted> implements Repository<T> {
  readonly changes = new ChangeEmitter();

  constructor(
    protected readonly table: Table<T, string>,
    protected readonly clock: Clock,
  ) {}

  async get(id: string): Promise<T | undefined> {
    return alive(await this.table.get(id));
  }

  async put(item: T): Promise<void> {
    await this.table.put(item);
    this.changes.emit();
  }

  async remove(id: string): Promise<void> {
    const row = await this.table.get(id);
    if (!row || row.deleted) return;
    await this.table.put({ ...row, deleted: true, updatedAt: this.clock.now().toISOString() });
    this.changes.emit();
  }

  async exportAll(): Promise<T[]> {
    return this.table.toArray();
  }

  async importAll(rows: T[]): Promise<void> {
    await this.table.db.transaction("rw", this.table, async () => {
      await this.table.clear();
      await this.table.bulkPut(rows);
    });
    this.changes.emit();
  }
}

const byDate = (a: Entry, b: Entry) => compareDateKeys(a.date, b.date) || a.createdAt.localeCompare(b.createdAt);

class DexieEntryRepository extends DexieRepository<Entry> implements EntryRepository {
  async byDate(subscriptionId: string, date: DateKey): Promise<Entry | undefined> {
    return (await this.listByDate(subscriptionId, date))[0];
  }

  async listByDate(subscriptionId: string, date: DateKey): Promise<Entry[]> {
    const rows = await this.table.where({ subscriptionId, date }).toArray();
    return rows.filter((r) => !r.deleted).sort(byDate);
  }

  async listBySubscription(subscriptionId: string, range?: DateRange): Promise<Entry[]> {
    const rows = range
      ? await this.table
          .where("[subscriptionId+date]")
          .between([subscriptionId, range.from], [subscriptionId, range.to], true, true)
          .toArray()
      : await this.table.where("subscriptionId").equals(subscriptionId).toArray();
    return rows.filter((r) => !r.deleted).sort(byDate);
  }

  async listInRange(range: DateRange): Promise<Entry[]> {
    const rows = await this.table.where("date").between(range.from, range.to, true, true).toArray();
    return rows.filter((r) => !r.deleted).sort(byDate);
  }
}

class DexieSubscriptionRepository extends DexieRepository<Subscription> implements SubscriptionRepository {
  async listAll(): Promise<Subscription[]> {
    const rows = await this.table.toArray();
    return rows.filter((r) => !r.deleted).sort((a, b) => compareDateKeys(a.startedAt, b.startedAt) || a.createdAt.localeCompare(b.createdAt));
  }
}

class DexieSettingsRepository implements SettingsRepository {
  readonly changes = new ChangeEmitter();

  constructor(private readonly table: Table<SettingsRow, string>) {}

  async get(): Promise<UserSettings> {
    const row = await this.table.get("user");
    if (!row) return { ...DEFAULT_SETTINGS };
    // Every stored field comes back (a list here once dropped localReminders); only the row key and the retired flag stay.
    const settings: Partial<SettingsRow> = { ...row };
    delete settings.id;
    delete settings.onboardingDone;
    return settings as UserSettings;
  }

  async update(patch: Partial<UserSettings>): Promise<UserSettings> {
    const next: UserSettings = { ...(await this.get()), ...patch };
    await this.table.put({ ...next, id: "user" });
    this.changes.emit();
    return next;
  }
}

export function createDexieStore(clock: Clock, name = "umemory"): Store {
  const db = new UmemoryDatabase(name);
  return {
    entries: new DexieEntryRepository(db.entries, clock),
    subscriptions: new DexieSubscriptionRepository(db.subscriptions, clock),
    scenarios: new BuiltInScenarioRepository(),
    settings: new DexieSettingsRepository(db.settings),
  };
}
